import { observer, Observer } from "mobx-react-lite";
import { useState, type ReactNode } from "react";

import { FolderScopeProvider } from "../ChooseEntityDialog/folder-scope-context-provider";
import { ConfirmationModal, ConfirmationModalVariant } from "../ConfirmationModal";

import useAvailableInputVariables from "./hooks/useAvailableInputVariables";
import useWebSocketListeners from "./hooks/useWebSocketListeners";
import Messages from "./Messages";
import { usePlayground } from "./playground-store";
import Drawer from "./Drawer";
import PlaygroundTourBridge from "./tour";

import type { PromptBlueprint, PromptVersion } from "@/types";
import type { InputVariableRow } from "@/types/playground";
import type { VariableSet } from "@/components/Playground/types";
import type { PlaygroundSessionSource } from "@/queries/playground/useUpdatePlaygroundSession";

interface PlaygroundCoreProps {
  blueprint?: Partial<PromptBlueprint>;
  inputVariables?: InputVariableRow[];
  inputVariableSets?: VariableSet[];
  selectedTemplateVersion?: PromptVersion;
  readonly?: boolean;
  editor?: boolean;
  isSnippet?: boolean;
  instanceId?: string;
  request_log_ids?: number[];
  snippet_overrides?: Record<string, string>;
  source?: PlaygroundSessionSource;
  editorActions?: ReactNode;
  footerLeftContent?: ReactNode;
}

const PlaygroundCore = ({ editorActions, footerLeftContent }: PlaygroundCoreProps) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const playground = usePlayground();
  useWebSocketListeners();
  useAvailableInputVariables();

  return (
    <FolderScopeProvider value={playground.selectedTemplateVersion?.folder_id ?? null}>
      <Observer>
        {() => (
          <ConfirmationModal
            title="Unsaved Changes"
            description="You have unsaved changes. Are you sure you want to leave?"
            handleOnConfirm={() => {
              setIsConfirmModalOpen(false);
            }}
            useBlocker={!playground.readonly}
            isNavigationBlocked={playground.hasBeenEdited}
            isOpen={isConfirmModalOpen}
            setIsOpen={setIsConfirmModalOpen}
            leaveButtonText="Discard Changes"
            saveButtonText="Cancel"
            headerVariant={ConfirmationModalVariant.warning}
          />
        )}
      </Observer>
      <div className="relative flex min-h-0 w-full flex-1 flex-col overflow-visible">
        {playground.readonly || playground.isSnippet ? (
          <Messages
            messages={playground.messages}
            onAddMessage={playground.addEmptyMessage}
            editorActions={editorActions}
            footerLeftContent={footerLeftContent}
          />
        ) : (
          <>
            <div className="min-h-0 flex-1 overflow-hidden">
              <Messages
                messages={playground.messages}
                onAddMessage={playground.addEmptyMessage}
                editorActions={editorActions}
                footerLeftContent={footerLeftContent}
              />
            </div>
            <Drawer />
          </>
        )}
      </div>
      <PlaygroundTourBridge />
    </FolderScopeProvider>
  );
};

export default observer(PlaygroundCore);
